import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Clock, Users, MapPin, DollarSign } from 'lucide-react';
const serviceData: Record<string, any> = {
  'full-service-events': {
    title: 'Full-Service Events & Weddings',
    tag: 'Celebrations',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=2000',
    description: "From intimate private parties to grand weddings and community events, we provide full-service catering tailored to your vision. All our set-up services include luxe decorations to elevate your event's atmosphere.",
    features: ['Custom Menu Development based on preferences', 'Fusion of Diverse Cuisines', 'Luxe Decorations Included with Set-up', 'Partnerships with Event Planners', 'Special Dietary Options Available'],
    stats: [{
      label: 'Event Types',
      value: 'Weddings & Socials',
      icon: Users
    }, {
      label: 'Scale',
      value: '10 to 100+ Guests',
      icon: Clock
    }, {
      label: 'Style',
      value: 'Luxe Set-ups',
      icon: MapPin
    }],
    packages: [{
      name: 'Weddings Package',
      selections: 'Up to 4 Entrees, 4 Sides, 2 Apps, 4 Hand-crafted Drinks',
      groups: [{
        range: '10-15 Guests',
        price: '$4,500 - $6,500'
      }, {
        range: '20-40 Guests',
        price: '$6,800 - $8,500'
      }, {
        range: '40-60 Guests',
        price: '$9,000 - $12,500'
      }, {
        range: '60-100 Guests',
        price: '$15,000 - $25,000'
      }]
    }, {
      name: 'Luxe Buffet',
      selections: 'Up to 3 Entrees, 3 Sides, 1 App',
      groups: [{
        range: '10-15 Guests',
        price: '$2,000 - $4,000'
      }, {
        range: '20-40 Guests',
        price: '$4,500 - $6,800'
      }, {
        range: '40-60 Guests',
        price: '$7,200 - $9,600'
      }, {
        range: '60-100 Guests',
        price: '$9,800 - $15,000'
      }]
    }, {
      name: 'Luxe Canapes & Drinks',
      selections: 'Up to 5 Apps, 4 Drinks',
      groups: [{
        range: '10-15 Guests',
        price: '$1,500 - $2,500'
      }, {
        range: '20-40 Guests',
        price: '$2,800 - $4,800'
      }, {
        range: '40-60 Guests',
        price: '$5,000 - $7,000'
      }, {
        range: '60-100 Guests',
        price: '$7,500 - $12,000'
      }]
    }, {
      name: 'Private Chef Experience',
      selections: 'Selections from Entree, Brunch, Beverages',
      pricing: '$200 per person'
    }]
  },
  'corporate-drop-off': {
    title: 'Corporate & Drop-Off Catering',
    tag: 'Business',
    image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&q=80&w=2000',
    description: 'Impress your clients and energize your team with our professional corporate catering. Whether you need a seamless drop-off service or a full-service setup (which always includes luxe decorations), we bring excellence to your business events.',
    features: ['Drop-Off & Full-Service Options', 'Luxe Decorations Included with Set-up', 'Customized Corporate Menus', 'Timely Delivery & Setup', 'Vegan, Gluten-Free & Halal Options'],
    stats: [{
      label: 'Clientele',
      value: 'Corporate & B2B',
      icon: Users
    }, {
      label: 'Service',
      value: 'Drop-off or Full',
      icon: Clock
    }, {
      label: 'Availability',
      value: 'DMV Area',
      icon: MapPin
    }],
    packages: [{
      name: 'Corporate - Office Meal',
      selections: 'Selections from Entree, Sides, Brunch, Beverages',
      pricing: '$65 per person'
    }, {
      name: 'Standard Drop-off Delivery',
      selections: 'Selections from Entree, Sides, Brunch',
      pricing: '$40 per person'
    }, {
      name: 'Luxe Buffet (Corporate Set-up)',
      selections: 'Up to 3 Entrees, 3 Sides, 1 App',
      groups: [{
        range: '10-15 Guests',
        price: '$2,000 - $4,000'
      }, {
        range: '20-40 Guests',
        price: '$4,500 - $6,800'
      }, {
        range: '40-60 Guests',
        price: '$7,200 - $9,600'
      }, {
        range: '60-100 Guests',
        price: '$9,800 - $15,000'
      }]
    }]
  },
  'meal-prep': {
    title: 'Meal Prep & Subscriptions',
    tag: 'Wellness',
    image: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?auto=format&fit=crop&q=80&w=2000',
    description: 'Designed for individuals who do not have the luxury to prepare healthy meals due to their busy schedules. Our meal prep and subscription services offer a fusion of authentic flavors with special attention to dietary preferences.',
    features: ['Weekly Subscription Services', 'Special Dietary Options (Vegan, GF, Halal)', 'Healthy & Balanced Portions', 'Fusion of Diverse Cuisines', 'Convenient Doorstep Delivery'],
    stats: [{
      label: 'Target',
      value: 'Busy Individuals',
      icon: Users
    }, {
      label: 'Options',
      value: 'Vegan, GF, Halal',
      icon: Clock
    }, {
      label: 'Frequency',
      value: 'Weekly Subs',
      icon: MapPin
    }],
    packages: [{
      name: 'Standard Drop-off Delivery (Meal Prep)',
      selections: 'Selections from Entree, Sides, Brunch',
      pricing: '$40 per person / per delivery'
    }, {
      name: 'Private Chef Experience',
      selections: 'In-home preparation of Entrees, Brunch, Beverages',
      pricing: '$200 per person'
    }]
  }
};
export function ServiceDetail({
  openQuote
}: {
  openQuote?: (type?: string) => void;
}) {
  const {
    id
  } = useParams();
  const service = serviceData[id || ''];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  if (!service) return <div className="py-40 text-center" data-id="element-628">Service not found.</div>;
  return <div className="bg-white min-h-screen" data-id="element-629">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh] w-full overflow-hidden" data-id="element-630">
        <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-631" />
        <div className="absolute inset-0 bg-brand-black/50" data-id="element-632" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4 md:px-6" data-id="element-633">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-634">
            <span className="text-brand-gold font-bold tracking-[0.2em] md:tracking-[0.3em] uppercase text-[10px] md:text-xs mb-4 block" data-id="element-635">
              {service.tag}
            </span>
            <h1 className="text-white text-4xl sm:text-5xl md:text-7xl font-serif" data-id="element-636">
              {service.title}
            </h1>
          </motion.div>
        </div>
        <Link to="/" className="absolute top-24 md:top-32 left-4 md:left-8 text-white flex items-center gap-2 hover:text-brand-gold transition-colors z-10 text-sm md:text-base" data-id="element-637">
          <ArrowLeft size={20} data-id="element-638" /> Back to Home
        </Link>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 md:px-6" data-id="element-639">
        <div className="grid lg:grid-cols-3 gap-12 md:gap-16" data-id="element-640">
          <div className="lg:col-span-2" data-id="element-641">
            <h2 className="text-2xl md:text-3xl font-serif mb-6 md:mb-8" data-id="element-642">
              Excellence in Every Detail
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 md:mb-12" data-id="element-643">
              {service.description}
            </p>

            {/* Visual Pricing Guide */}
            <div className="mb-16" data-id="element-644">
              <h3 className="text-xl font-serif mb-6 text-brand-black uppercase tracking-widest" data-id="element-645">
                Visual Pricing Guide
              </h3>
              <div className="relative aspect-[16/10] overflow-hidden rounded-sm border border-gray-100 shadow-2xl" data-id="element-646">
                <img src="https://cdn.magicpatterns.com/uploads/oCxtfmG3XQ3rnHrEkVwDDY/Menu.png" alt="Catering Style Package Guide" className="w-full h-full object-cover" data-id="element-647" />
                <div className="absolute inset-0 bg-brand-black/5 hover:bg-transparent transition-colors duration-500" data-id="element-648" />
              </div>
              <p className="mt-4 text-sm text-gray-500 italic" data-id="element-649">
                * Click 'Request a Quote' to see specific details for your event
                size.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-650">
              {service.features.map((feature: string) => <div key={feature} className="flex gap-4 items-start" data-id="element-651">
                  <CheckCircle2 className="text-brand-gold shrink-0" size={24} data-id="element-652" />
                  <span className="text-gray-700 font-medium" data-id="element-653">{feature}</span>
                </div>)}
            </div>

            <h2 className="text-2xl md:text-3xl font-serif mb-8 border-b border-gray-100 pb-4" data-id="element-654">
              Our Packages
            </h2>
            <div className="space-y-6" data-id="element-655">
              {service.packages.map((pkg: any, idx: number) => <div key={idx} className="bg-gray-50 p-6 md:p-8 rounded-sm border border-gray-100" data-id="element-656">
                  <h3 className="text-xl font-bold text-brand-black mb-2" data-id="element-657">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-600 mb-6 text-sm" data-id="element-658">{pkg.selections}</p>

                  {pkg.groups ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-659">
                      {pkg.groups.map((group: any, gIdx: number) => <div key={gIdx} className="bg-white p-4 rounded-sm border border-gray-200 flex justify-between items-center" data-id="element-660">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400" data-id="element-661">
                            {group.range}
                          </span>
                          <span className="text-sm font-bold text-brand-black" data-id="element-662">
                            {group.price}
                          </span>
                        </div>)}
                    </div> : <div className="flex items-start gap-3 bg-white p-4 rounded-sm border border-gray-200" data-id="element-663">
                      <DollarSign className="text-brand-gold shrink-0 mt-0.5" size={18} data-id="element-664" />
                      <p className="text-sm font-medium text-gray-800 leading-relaxed" data-id="element-665">
                        {pkg.pricing}
                      </p>
                    </div>}
                  <p className="mt-4 text-[10px] uppercase tracking-widest text-gray-400 font-bold" data-id="element-666">
                    * All set-up services include luxe decorations
                  </p>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-1" data-id="element-667">
            <div className="bg-gray-50 p-8 rounded-sm border border-gray-100 sticky top-32" data-id="element-668">
              <h3 className="text-xl font-serif mb-8 border-b border-gray-200 pb-4" data-id="element-669">
                Service Overview
              </h3>
              <div className="space-y-8" data-id="element-670">
                {service.stats.map((stat: any) => <div key={stat.label} className="flex gap-4" data-id="element-671">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-brand-gold shrink-0" data-id="element-672">
                      <stat.icon size={20} data-id="element-673" />
                    </div>
                    <div data-id="element-674">
                      <p className="text-xs text-gray-400 uppercase tracking-widest mb-1" data-id="element-675">
                        {stat.label}
                      </p>
                      <p className="font-bold text-brand-black" data-id="element-676">{stat.value}</p>
                    </div>
                  </div>)}
              </div>
              <button onClick={() => openQuote && openQuote(service.title)} className="w-full bg-brand-gold text-white py-4 rounded-sm font-bold uppercase tracking-widest mt-12 hover:bg-brand-black transition-all" data-id="element-677">
                Request a Quote
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}